import { SessionContext } from '@contexts/session-context';
import { Modal, Tab, TabItem, useTranslation } from '@testsigma/atoms';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UsageCount } from './count';
import { UsageHeader } from './header';
import { UsageTable } from './table';
import { LicenseUsageDetailsService } from '@services/license-usage-details.service';
import { LicenseUsageDetails } from '@modules/test-development/models/license-usage.model';
import {
  getAgentIDsFromAgentList,
  useReportAgentModal,
  ReportAgentModal,
  ReportAgentContext,
} from '@modules/agent/report-agent';

interface UsageDetailsPros {
  isUsageModalOpen: boolean;
  setIsUsageModalOpen: (isOpen: boolean) => void;
}

export const UsageDetails: React.FC<UsageDetailsPros> = ({
  isUsageModalOpen,
  setIsUsageModalOpen,
}) => {
  const { session } = useContext(SessionContext);
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [currentTab, updateCurrentTab] = useState('testplan');
  const [testPlanId, setTestPlanId] = useState<number | string | null>(null);
  const [licenseUsageDetails, setLicenseUsageDetails] =
    useState<LicenseUsageDetails>();
  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _,
    {
      state: { show: showReportAgentModal },
      closeModal: onCloseReportAgentModal,
    },
    openModal,
  ] = useReportAgentModal();
  const { testPlanIdVsAgents } = useContext(ReportAgentContext);

  const fetchLicenseUsageDetails = useCallback(() => {
    setIsRefreshing(true);
    LicenseUsageDetailsService.show()
      .then((res) => {
        setLicenseUsageDetails(res);
        setIsRefreshing(false);
        if (res?.subscription && session?.tenant) {
          session.tenant.subscription = res.subscription;
        }
      })
      .catch((error) => {
        setIsRefreshing(false); // Ensure refreshing is set to false even if there's an error
      });
  }, [session]);

  const refresh = useCallback(async () => {
    fetchLicenseUsageDetails();
  }, [fetchLicenseUsageDetails]);

  const handleTab = (current: string) => {
    updateCurrentTab(current);
  };

  useEffect(() => {
    if (!isUsageModalOpen) return;
    refresh();
  }, [isUsageModalOpen, refresh]);
  const onClickReportAgent = (testPlanId) => (e) => {
    e.preventDefault();
    setTestPlanId(testPlanId);
    openModal(true);
  };

  return (
    <>
      <Modal
        isOpen={isUsageModalOpen}
        className='!rounded-lg !min-w-10/12 text-unique-600 min-h-11/12 h-11/12 max-w-10/12'
        closeOnEscape={true}
        closeOnOutsideClick={true}
        onClose={() => setIsUsageModalOpen(false)}>
        <UsageHeader
          subscription={session?.tenant?.subscription}
          refresh={refresh}
          isRefreshing={isRefreshing}
          close={() => setIsUsageModalOpen(false)}
        />

        <UsageCount
          subscription={session?.tenant?.subscription}
          licenseUsageDetails={licenseUsageDetails}
        />

        <div className='border border-secondary-100 rounded-md h-[calc(100%_-_132px)]'>
          <div>
            <Tab direction='horizontal' type='primary' className='flex-shrink'>
              <TabItem
                variant='secondary'
                isSelected={currentTab == 'testplan'}
                onClick={() => handleTab('testplan')}
                count={
                  licenseUsageDetails?.testPlanLicenseUsageDetails?.length ||
                  null
                }
                className={'px-4'}
                countClassName={
                  '!bg-primary-50 !text-primary-700 !font-medium'
                }>
                {t('usage_details.plan_dry_run')}
              </TabItem>
              <TabItem
                variant='secondary'
                isSelected={currentTab == 'mobileInspection'}
                onClick={() => handleTab('mobileInspection')}
                count={
                  licenseUsageDetails?.mobileInspectionLicenseUsageDetails
                    ?.length || null
                }
                className={'px-4'}
                countClassName={
                  '!bg-primary-50 !text-primary-700 !font-medium'
                }>
                {t('usage_details.mobile_session')}
              </TabItem>
              <TabItem
                variant='secondary'
                isSelected={currentTab == 'debuggingSession'}
                onClick={() => handleTab('debuggingSession')}
                count={
                  licenseUsageDetails?.debuggerUsageDetails?.length || null
                }
                className={'px-4'}
                countClassName={
                  '!bg-primary-50 !text-primary-700 !font-medium'
                }>
                {t('usage_details.debugging_session')}
              </TabItem>
            </Tab>
          </div>
          <div className='pt-4 h-[calc(100%_-_44px)]'>
            <UsageTable
              activeTab={currentTab}
              testPlanLicenseUsageList={
                licenseUsageDetails?.testPlanLicenseUsageDetails
              }
              inspections={
                licenseUsageDetails?.mobileInspectionLicenseUsageDetails
              }
              debuggingSessionUsageList={
                licenseUsageDetails?.debuggerUsageDetails
              }
              refresh={refresh}
              onClose={setIsUsageModalOpen}
              onClickReportAgent={onClickReportAgent}
              testPlanIdVsAgents={testPlanIdVsAgents}
            />
          </div>
        </div>
      </Modal>

      {showReportAgentModal && (
        <ReportAgentModal
          isOpen={showReportAgentModal}
          onClose={onCloseReportAgentModal}
          agentIds={getAgentIDsFromAgentList(testPlanIdVsAgents[testPlanId])}
        />
      )}
    </>
  );
};
